// @flow
/** @jsx jsx */

import * as React from 'react';

import Text from '@zapier/common-components/src/components/Text';
import Icon, {
  type IconNameType,
} from '@zapier/common-components/src/components/Icon';
import { border } from '@zapier/common-components/src/components/Tabs/constants';
import { Colors, Duration } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

type TabTitleProps = {
  /** Tab title contents */
  children?: React.Node,
  /** Icon to show to left of title */
  icon?: IconNameType,
  /** unique tab id (auto-generated when inside Tabs) */
  id: string,
  /** Tab is active */
  isActive?: boolean,
  /** Handler for when tab is clicked */
  onClick: () => void,
  /** Handler for onKeyDown event */
  onKeyDown: (e: SyntheticKeyboardEvent<>) => void,
};

const iconCss = css({
  marginRight: '10px',
});

const transition = `${Duration.short} ease-in-out`;

const tabTitleBaseCss = css({
  alignItems: 'center',
  border: 'none',
  borderRight: border,
  cursor: 'pointer',
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'center',
  margin: 0,
  outline: 'none',
  padding: '12px 12px 17px 12px',
  transition: `background-color ${transition}, border-bottom ${transition}, color ${transition}`,

  ':last-of-type': {
    borderRight: 'none',
  },

  backgroundColor: Colors.storm,
  backgroundImage: 'none', // FIXME(ruebel): Remove once zapier/zapier global styles are cleared
  borderBottom: border,
  borderRadius: 0, // FIXME(ruebel): Remove once zapier/zapier global styles are cleared
  color: Colors.heroine,

  '&:focus, &:hover': {
    backgroundColor: Colors.theme.secondary.dark,
    backgroundImage: 'none', // FIXME(ruebel): Remove once zapier/zapier global styles are cleared
  },
});

const tabTitleIsActiveCss = css({
  backgroundColor: Colors.white,
  borderBottom: 'none',
  color: Colors.deepSpace,

  '&:focus, &:hover': {
    backgroundColor: Colors.white,
    backgroundImage: 'none', // FIXME(ruebel): Remove once zapier/zapier global styles are cleared
    color: Colors.deepSpace,
  },
});

/**
 * For use inside of the `Tabs` component
 */
const TabTitle = ({
  children,
  icon,
  id,
  isActive = false,
  onClick,
  onKeyDown,
}: TabTitleProps) => {
  const tabTitleCss = css(tabTitleBaseCss, isActive && tabTitleIsActiveCss);

  return (
    <button
      aria-controls={id}
      aria-selected={isActive}
      css={tabTitleCss}
      id={`${id}-title`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="tab"
    >
      {icon && <Icon css={iconCss} name={icon} size={12} />}
      {children && (
        <Text color={Colors.inherit} type="subsection-head">
          {children}
        </Text>
      )}
    </button>
  );
};

export default TabTitle;
